import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, useParams } from 'react-router-dom';
import { AuthProvider } from './components/self-service/auth/useAuth';
import DevMenu from './components/self-service/DevMenu';
import HelpMenu from './components/self-service/help/HelpMenu';
import AdminShell from './components/self-service/admin/AdminShell';
import BrowseChannelsPage from './pages/self-service/BrowseChannelsPage';
import SelfServicePage from './pages/self-service/SelfServicePage';
import SubmittedPage from './pages/self-service/SubmittedPage';
import MyCampaignsPage from './pages/self-service/MyCampaignsPage';
import AdvertiserCampaignDetailPage from './pages/self-service/AdvertiserCampaignDetailPage';
import CampaignReportPage from './pages/self-service/CampaignReportPage';
import UpdatePaymentPage from './pages/self-service/UpdatePaymentPage';
import AdvertiserCreativeLibraryPage from './pages/self-service/AdvertiserCreativeLibraryPage';
import PublisherAdminPage from './pages/self-service/PublisherAdminPage';
import PublisherPaymentsPage from './pages/self-service/PublisherPaymentsPage';
import PublisherSubmissionsPage from './pages/self-service/PublisherSubmissionsPage';
import PublisherSubmissionDetailPage from './pages/self-service/PublisherSubmissionDetailPage';
import PublisherAdvertisersPage from './pages/self-service/PublisherAdvertisersPage';
import PublisherReferralsPage from './pages/self-service/PublisherReferralsPage';
import PublisherReferralDetailPage from './pages/self-service/PublisherReferralDetailPage';
import PublisherPromotionsPage from './pages/self-service/PublisherPromotionsPage';
import PublisherPromotionDetailPage from './pages/self-service/PublisherPromotionDetailPage';
import PublisherChannelSettingsPage from './pages/self-service/PublisherChannelSettingsPage';
import PublisherOnboardPage from './pages/self-service/PublisherOnboardPage';
import PublisherPublicaPushJobsPage from './pages/self-service/PublisherPublicaPushJobsPage';
import PublisherPublicaPushJobDetailPage from './pages/self-service/PublisherPublicaPushJobDetailPage';
import AdminHelpCenterPage from './pages/self-service/AdminHelpCenterPage';
import HelpCenterPage from './pages/self-service/HelpCenterPage';
import InvitationLandingPage from './pages/self-service/InvitationLandingPage';
import PublisherTeamPage from './pages/self-service/PublisherTeamPage';
import StationInviteAcceptPage from './pages/self-service/StationInviteAcceptPage';
import AccessDeniedPage from './pages/self-service/AccessDeniedPage';
import ResetPasswordPage from './pages/self-service/ResetPasswordPage';
import AdminEntryPage from './pages/self-service/AdminEntryPage';
import SettingsLayout from './pages/settings/SettingsLayout';
import SettingsBusinessTab from './pages/settings/SettingsBusinessTab';
import SettingsTeamTab from './pages/settings/SettingsTeamTab';
import SettingsSecurityTab from './pages/settings/SettingsSecurityTab';
import * as analytics from './lib/analytics';
import { isSelfServiceDevelopment } from './lib/env';
import './index.css';

const wrap = (el: React.ReactNode) => (
  <AuthProvider>
    {el}
    <HelpMenu />
    <DevMenu />
  </AuthProvider>
);

// Redirect legacy /:slug/my-team bookmarks to the consolidated Settings ->
// Team tab. Uses useParams so the slug survives into the Navigate target.
// eslint-disable-next-line react-refresh/only-export-components -- routing indirection, not a shared component
const MyTeamLegacyRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={slug ? `/${slug}/settings/team` : '/'} replace />;
};

const router = createBrowserRouter([
  // Closed-loop per-station advertiser dashboard. Legacy /my-campaigns(/...)
  // URLs from old emails redirect to root (BrowseChannels), which sends the
  // user back into a station context.
  { path: '/:slug/my-campaigns', element: wrap(<MyCampaignsPage />) },
  { path: '/:slug/my-campaigns/:submissionId/update-payment', element: wrap(<UpdatePaymentPage />) },
  { path: '/:slug/my-campaigns/:submissionId', element: wrap(<AdvertiserCampaignDetailPage />) },
  // CTV-445: dedicated performance report (delivery + geo + daypart + creative).
  { path: '/:slug/my-campaigns/:submissionId/report', element: wrap(<CampaignReportPage />) },
  { path: '/my-campaigns', element: <Navigate to="/" replace /> },
  { path: '/my-campaigns/:submissionId', element: <Navigate to="/" replace /> },
  // CTV-457 Phase B: cross-station Library of commercials the user has
  // submitted. Lives under :slug so the in-loop chrome (PublicHeader, sign-
  // out fallback) renders with station branding even though the underlying
  // data is per-user across all stations.
  { path: '/:slug/library', element: wrap(<AdvertiserCreativeLibraryPage />) },
  // Consolidated advertiser Settings surface (business / team / security).
  // SettingsLayout owns the PublicHeader + tab strip; each sub-route
  // renders as an <Outlet /> inside it.
  {
    path: '/:slug/settings',
    element: wrap(<SettingsLayout />),
    children: [
      { index: true, element: <Navigate to="business" replace /> },
      { path: 'business', element: <SettingsBusinessTab /> },
      { path: 'team', element: <SettingsTeamTab /> },
      { path: 'security', element: <SettingsSecurityTab /> },
    ],
  },
  // Legacy /my-team bookmarks land on the new Team tab.
  { path: '/:slug/my-team', element: <MyTeamLegacyRedirect /> },
  { path: '/my-team', element: <Navigate to="/" replace /> },
  // Pre-consolidation Security URL is unrouted without a slug — bounce home.
  { path: '/settings/security', element: <Navigate to="/" replace /> },
  { path: '/invitations/:token', element: wrap(<InvitationLandingPage />) },
  { path: '/station-invites/:token', element: wrap(<StationInviteAcceptPage />) },
  { path: '/access-denied', element: wrap(<AccessDeniedPage />) },
  { path: '/reset-password', element: wrap(<ResetPasswordPage />) },

  // Staff admin. /admin = entry/picker. /admin/onboard = super_admin channel
  // creation (top-level — no station context). /admin/:slug = per-station
  // shell with all sub-views as nested children. /admin/all/* is reserved
  // for the future aggregated view; today an unknown slug falls through the
  // gate's redirect, so /admin/all has no effect until the shell handles it.
  { path: '/admin', element: wrap(<AdminEntryPage />) },
  { path: '/admin/onboard', element: wrap(<PublisherOnboardPage />) },
  { path: '/admin/publica-push-jobs', element: wrap(<PublisherPublicaPushJobsPage />) },
  { path: '/admin/publica-push-jobs/:jobId', element: wrap(<PublisherPublicaPushJobDetailPage />) },
  { path: '/admin/help-center', element: wrap(<AdminHelpCenterPage />) },
  {
    path: '/admin/:slug',
    element: wrap(<AdminShell />),
    children: [
      { index: true, element: <PublisherAdminPage /> },
      { path: 'payments', element: <PublisherPaymentsPage /> },
      { path: 'submissions', element: <PublisherSubmissionsPage /> },
      { path: 'submissions/:submissionId', element: <PublisherSubmissionDetailPage /> },
      { path: 'advertisers', element: <PublisherAdvertisersPage /> },
      { path: 'team', element: <PublisherTeamPage /> },
      { path: 'referrals', element: <PublisherReferralsPage /> },
      { path: 'referrals/:salespersonId', element: <PublisherReferralDetailPage /> },
      { path: 'promotions', element: <PublisherPromotionsPage /> },
      { path: 'promotions/:promotionId', element: <PublisherPromotionDetailPage /> },
      { path: 'channel-settings', element: <PublisherChannelSettingsPage /> },
    ],
  },

  // Public + advertiser-facing wizard routes.
  // CTV-593: the index route (BrowseChannels) is dev-only. Prod is white-
  // labeled, so users only ever enter through /{station}. Hitting `/` in
  // prod falls through to React Router's default no-match (404).
  ...(isSelfServiceDevelopment()
    ? [{ path: '/', element: wrap(<BrowseChannelsPage />) }]
    : []),
  { path: '/:slug', element: wrap(<SelfServicePage />) },
  { path: '/:slug/start', element: wrap(<SelfServicePage />) },
  { path: '/:slug/start/:step', element: wrap(<SelfServicePage />) },
  { path: '/:slug/submitted/:submissionId', element: wrap(<SubmittedPage />) },
  { path: '/:slug/help', element: wrap(<HelpCenterPage />) },
]);

// Analytics: init both providers (each no-ops without its key), then fire a
// SPA pageview on every route change. router.subscribe doesn't emit for the
// initial load, so we send that one explicitly.
analytics.init();
let lastTrackedPath = window.location.pathname;
analytics.page(lastTrackedPath);
router.subscribe((state) => {
  if (state.navigation.state === 'idle' && state.location.pathname !== lastTrackedPath) {
    lastTrackedPath = state.location.pathname;
    analytics.page(lastTrackedPath);
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
);
